import type { ScenarioDelta } from '../../../core/data-profiles/scenarios/ScenarioDelta';
import type {
  PositionIntentCore,
  UnsupportedDeltaOp,
} from './scenarioDeltaToPositionIntents';
import {
  normalizeOrgPersonFieldValue,
  orgPersonWritableField,
} from '../fields/orgPersonWritableFields';
import { lexicalNotesToMarkdown } from '../../../lib/richText/lexicalNotes';

/**
 * Canvas fields that represent canonical entity facts rather than Position
 * structure. Add future canvas-to-domain aliases here without expanding the
 * responsibility of scenarioDeltaToPositionIntents.
 */
const PERSON_FIELD_ALIASES: Readonly<Record<string, string>> = {
  notesRich: 'notes',
};

export interface ScenarioDeltaFieldTranslationInput {
  readonly delta: ScenarioDelta;
  readonly recordIdByNodeId: (nodeId: string) => string | undefined;
  readonly isPersonRecord?: (recordId: string) => boolean;
}

export interface ScenarioDeltaFieldTranslation {
  readonly intents: readonly Extract<PositionIntentCore, { kind: 'update_person' }>[];
  readonly unsupported: readonly UnsupportedDeltaOp[];
}

export function canonicalScenarioField(field: string): string | undefined {
  return PERSON_FIELD_ALIASES[field];
}

/** Return a delta containing only operations owned by the Position translator. */
export function withoutScenarioFieldUpdates(delta: ScenarioDelta): ScenarioDelta {
  return {
    ...delta,
    updates: delta.updates.filter((update) => !canonicalScenarioField(update.change?.field ?? '')),
  };
}

/** Translate durable entity-field edits into canonical Person update intents. */
export function scenarioDeltaToFieldIntents(
  input: ScenarioDeltaFieldTranslationInput,
): ScenarioDeltaFieldTranslation {
  const intentsByRecordId = new Map<string, Record<string, string>>();
  const unsupported: UnsupportedDeltaOp[] = [];

  for (const update of input.delta.updates) {
    const nodeId = String(update.nodeId);
    const sourceField = update.change?.field;
    const canonicalField = sourceField ? canonicalScenarioField(sourceField) : undefined;
    if (!canonicalField) continue;

    const manifest = orgPersonWritableField(canonicalField);
    const newValue = update.change?.newValue;
    const recordId = input.recordIdByNodeId(nodeId);
    if (!manifest || typeof newValue !== 'string' || !recordId || !input.isPersonRecord?.(recordId)) {
      unsupported.push({
        op: 'update',
        nodeId,
        reason: `field "${sourceField ?? '?'}" is not committable for a canonical Person record`,
      });
      continue;
    }

    const fields = intentsByRecordId.get(recordId) ?? {};
    fields[manifest.id] = sourceField === 'notesRich'
      ? lexicalNotesToMarkdown(newValue)
      : normalizeOrgPersonFieldValue(newValue) as string;
    intentsByRecordId.set(recordId, fields);
  }

  return {
    intents: [...intentsByRecordId].map(([personRecordId, fields]) => ({
      kind: 'update_person' as const,
      personRecordId,
      fields,
    })),
    unsupported,
  };
}
